import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Printer, Camera, Compass, Battery, Shirt, Flame, Bug, Flashlight, Map, Droplets, Wrench, Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getTripClimateProfile, type ClimateZone, type Season } from '@/utils/climateClassification';
import { getFilteredPackingList, type PackingCategory, type PackingPriority } from '@/data/packingList';

interface PackingListProps {
  stops: Array<{ latitude: number; longitude: number; arrival_date: string; location_name: string }>;
}

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  Camera, Compass, Battery, Shirt, Flame, Bug, Flashlight, Map, Droplets, Wrench, Lightbulb,
};

const ZONE_LABELS: Record<ClimateZone, { label: string; color: string }> = {
  desert: { label: 'Desert', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  alpine: { label: 'Alpine', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  tropical: { label: 'Tropical', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  coastal: { label: 'Coastal', color: 'bg-sky-100 text-sky-800 border-sky-200' },
  highAltitude: { label: 'High Altitude', color: 'bg-violet-100 text-violet-800 border-violet-200' },
  continental: { label: 'Temperate', color: 'bg-slate-100 text-slate-800 border-slate-200' },
};

const SEASON_LABELS: Record<Season, { label: string; color: string }> = {
  winter: { label: 'Winter', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  spring: { label: 'Spring', color: 'bg-green-50 text-green-700 border-green-200' },
  summer: { label: 'Summer', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  fall: { label: 'Fall', color: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const PRIORITY_STYLES: Record<PackingPriority, string> = {
  essential: 'font-medium',
  recommended: '',
  optional: 'text-muted-foreground',
};

export const PackingList: React.FC<PackingListProps> = ({ stops }) => {
  const profile = getTripClimateProfile(stops);
  const categories = getFilteredPackingList(profile.conditions);

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Header with conditions */}
      <div className="space-y-3 print:space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold print:text-lg">Packing List</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            className="gap-2 print:hidden"
          >
            <Printer className="w-4 h-4" />
            Print
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Personalized for your trip based on destinations and dates.
        </p>
        <div className="flex flex-wrap gap-2">
          {profile.zones.map(zone => (
            <Badge key={zone} variant="outline" className={ZONE_LABELS[zone].color}>
              {ZONE_LABELS[zone].label}
            </Badge>
          ))}
          <Badge variant="outline" className={SEASON_LABELS[profile.season].color}>
            {SEASON_LABELS[profile.season].label}
          </Badge>
        </div>
      </div>

      {/* Categories */}
      {categories.map(category => {
        const IconComponent = ICON_MAP[category.icon];
        const essentials = category.items.filter(i => i.priority === 'essential');
        const others = category.items.filter(i => i.priority !== 'essential');

        return (
          <div key={category.name} className="space-y-2 print:break-inside-avoid">
            <div className="flex items-center gap-2 border-b pb-1">
              {IconComponent && <IconComponent className="w-4 h-4 text-muted-foreground" />}
              <h3 className="font-semibold text-sm">{category.name}</h3>
              <span className="text-xs text-muted-foreground">({category.items.length} items)</span>
            </div>
            <ul className="space-y-1 text-sm">
              {[...essentials, ...others].map(item => (
                <li key={item.name} className={`flex items-start gap-2 py-0.5 ${PRIORITY_STYLES[item.priority]}`}>
                  <span className="shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-foreground/40" />
                  <div className="flex-1 min-w-0">
                    <span>{item.name}</span>
                    {item.description && (
                      <span className="text-muted-foreground ml-1">
                        — {item.description}
                      </span>
                    )}
                    {item.resourcesLink && (
                      <Link
                        to={item.resourcesLink}
                        className="inline-flex items-center gap-0.5 ml-2 text-xs text-primary hover:underline print:hidden"
                      >
                        Our picks <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                  {item.conditions.length > 0 && (
                    <div className="flex gap-1 shrink-0 print:hidden">
                      {item.conditions
                        .filter(c => profile.conditions.includes(c))
                        .slice(0, 2)
                        .map(c => {
                          const style = c in ZONE_LABELS
                            ? ZONE_LABELS[c as ClimateZone]
                            : SEASON_LABELS[c as Season];
                          return (
                            <span key={c} className={`text-[10px] px-1.5 py-0.5 rounded border ${style?.color || ''}`}>
                              {style?.label || c}
                            </span>
                          );
                        })}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      {/* Legend */}
      <div className="text-xs text-muted-foreground border-t pt-3 print:pt-2">
        <p><span className="font-medium">Bold items</span> are essential. Regular items are recommended. <span className="text-muted-foreground">Gray items</span> are optional.</p>
        <p className="mt-1">Condition badges show why an item is recommended for your specific trip.</p>
      </div>
    </div>
  );
};
